import {
    BOOLEAN_LABEL_KEYS,
    DISPLAY_OPTION_LABEL_KEYS,
    FIELD_ATTR_LABEL_KEYS,
    HIDDEN_FIELDS,
    PALETTE_LABEL_KEYS,
    PATH_LABEL_KEYS,
    REQUEST_TYPE_ATTR_LABEL_KEYS,
    TEMPLATE_ATTR_LABEL_KEYS,
    VALUE_LABEL_KEYS_BY_ATTR,
    WORKFLOW_VALUE_LABEL_KEYS,
} from './changeLabels';
import {
    NODE_TYPE_CONFIG,
    isBranchingNodeType,
} from '/b2b/dsarForms/routes/FormDetails/components/FormWorkflow/constants';

// The leaf module, not the barrel: the barrel pulls react-pdf into the browser bundle.
import { CHANGE_ARROW_CLASS } from '/b2b/common/helpers/ReactPDF/fonts';
import { PREVIOUS_OUTCOME_FIELD } from '/b2b/dsarForms/routes/FormDetails/components/FormWorkflow/components/AddDecisionDialog/constants';
import { getCustomFieldLabelFromPath } from '/b2b/dsarForms/routes/FormDetails/components/FormWorkflow/components/AddDecisionDialog/helpers';
import isEqual from 'lodash/isEqual';
import palette from '/b2b/common/theme/palette';
import startCase from 'lodash/startCase';
import { stripBBCodeTags } from '/b2b/common/components/Locale/formatters/bbcodeTags';
import { translate } from '/b2b/common/helpers/i18n';
import unescape from 'lodash/unescape';

/** Anything that is not an object. Null and undefined count: both show as an em dash. */
const isScalar = value => value === null || value === undefined || typeof value !== 'object';

/**
 * A `{ label, value }` option that a dialog select stored as it is; only its label shows. Both keys
 * are necessary, so a decision rule `{ path, operator, value }` still shows as the sub-list it is.
 */
const isOptionObject = value =>
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'label' in value &&
    'value' in value &&
    isScalar(value.label);

/** A value that goes in one chip: a scalar, an option, or a list of either on one line. */
const isChipValue = value =>
    isScalar(value) ||
    isOptionObject(value) ||
    (Array.isArray(value) && value.every(item => isScalar(item) || isOptionObject(item)));

/**
 * An object one level deep, of which each value is a chip: actionConfig and automationConfig. An
 * option object is not one, because it is a single value.
 */
const isFlatObject = value =>
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !isOptionObject(value) &&
    Object.values(value).every(isChipValue);

/** Translates a key if the vocabulary has one, or gives the fallback. */
const translateOr = (key, fallback) => (key ? translate(key) : fallback);

/** The keys of both sides of a diff. A key that one side added or removed needs a row too. */
const unionKeys = (a, b) => [...new Set([...Object.keys(a), ...Object.keys(b)])];

// "New value" chip colors (osano-50 bg / osano-800 fg).
const CHANGE_NEW_VALUE_BG = '#F5F0FF';
const CHANGE_NEW_VALUE_FG = '#3E1A7F';

// Each style is inline, because the markup goes into a BBCode [html] block.
const { grey } = palette.common;
const OLD_CHIP = `background-color:${grey[100]};border-radius:4px;padding:1px 6px;text-decoration:line-through;text-decoration-color:${grey[400]};`;
const NEW_CHIP = `background-color:${CHANGE_NEW_VALUE_BG};color:${CHANGE_NEW_VALUE_FG};border-radius:4px;padding:1px 6px;`;
const ARROW = `color:${grey[500]};margin:0 10px;`;
const LIST_STYLE = 'list-style:none;padding-left:0;margin:4px 0;';
const NESTED_LIST_STYLE = 'list-style:none;padding-left:16px;margin:4px 0;';
const ITEM_STYLE = 'margin-bottom:6px;';
/*
 * A chip carries a background, and the PDF renderer paints one only on a block box — an inline
 * span drops it, with or without the shorthand. So a chip is a `div` and the row that holds it
 * is a flex line, which is also how the browser drew it before.
 */
const ROW_STYLE = 'display:flex;flex-direction:row;align-items:center;flex-wrap:wrap;';
const ROW_LABEL_STYLE = 'font-weight:bold;margin-right:8px;';
// An entity's rows, behind a rail that ties them to the heading above them.
const GROUP_LIST_STYLE = `${NESTED_LIST_STYLE}border-left:2px solid ${grey[300]};`;
/*
 * The count is named rather than sized here. A fixed size cannot serve both readers: 12px sits
 * under the web's body text and over the PDF's smaller one, and the relative units that would
 * have solved it are dropped by the PDF renderer — `em` and `%` take the text with them, and
 * `small` has no renderer at all. So each medium sizes `.osano-change-count` against its own.
 */
const GROUP_COUNT_CLASS = 'osano-change-count';
const GROUP_COUNT_STYLE = `color:${grey[600]};`;

// A default template's name is itself an i18n key, so it is resolved; free text falls
// through. Three segments are required, or "acme.com" would read as a key and pollute
// the missing-key list translate() keeps.
const KEY_CHARS = /^[a-z0-9_.-]+$/i;
const isKeyShaped = label => {
    if (typeof label !== 'string' || !KEY_CHARS.test(label)) {
        return false;
    }

    const segments = label.split('.');
    return segments.length >= 3 && segments.every(Boolean);
};
const resolveLabel = label => (isKeyShaped(label) ? translate(label) : label);

// The per-entity path families: `{prefix}{key}[.{attr}]`.
const ENTITY_PREFIXES = ['fields.', 'requestTypes.', 'templates.'];

// The entity's key and the property it reports. A key can hold dots of its own, so the
// change's `id` is preferred over splitting; without one, the read is positional.
const splitEntityPath = (path, prefix, id) => {
    const rest = path.slice(prefix.length);
    const key = id === null || id === undefined ? '' : String(id);
    if (key && rest === key) {
        return { key, attr: null };
    }

    if (key && rest.startsWith(`${key}.`)) {
        return { key, attr: rest.slice(key.length + 1) };
    }

    const [first, second] = rest.split('.');
    // Flagged: a positional read cannot tell a dotted key from a nested property.
    return { key: first, attr: second ?? null, positional: true };
};

// The property a change reports: for a per-entity path the attribute after the
// entity's key, else the last segment (`settings.captchaEnabled` → captchaEnabled).
const changeAttribute = ({ path = '', id } = {}) => {
    const prefix = ENTITY_PREFIXES.find(entityPrefix => path.startsWith(entityPrefix));

    return prefix ? splitEntityPath(path, prefix, id).attr : path.split('.').pop() || null;
};

// "{entity}: {property}", or the entity alone when the change is the entity itself.
// `propertyOnly` drops the entity, for a sentence that already names it.
const entityLabel = (path, prefix, { id, label, attrLabelKeys, propertyOnly }) => {
    const { key, attr } = splitEntityPath(path, prefix, id);
    const base = label || startCase(key);
    if (!attr) {
        return base;
    }

    const attrLabelKey = attrLabelKeys?.[attr];
    const property = attrLabelKey ? translate(attrLabelKey) : startCase(attr);

    return propertyOnly ? property : `${base}: ${property}`;
};

// The entity a change edits, and the name to head its group with; null for anything that
// reads flat. The key keeps its prefix, or a field and a template sharing an id merge.
const entityOf = change => {
    const { path = '', id, label: rawLabel } = change ?? {};
    const prefix = ENTITY_PREFIXES.find(entityPrefix => path.startsWith(entityPrefix));
    if (!prefix) {
        return null;
    }

    const { key, attr, positional } = splitEntityPath(path, prefix, id);
    if (!key || !attr) {
        return null;
    }

    // A positional read keeps only the first segment, so `o-user.email` and `o-user.phone`
    // both report `o-user`; grouping there would file one field's edit under another's name.
    if (positional && path.slice(prefix.length).split('.').length > 2) {
        return null;
    }

    // `named`: the name came off the change, not from start-casing its key.
    return {
        key: `${prefix}${key}`,
        label: resolveLabel(rawLabel) || startCase(key),
        named: Boolean(rawLabel),
    };
};

// A change's label: translated for known setting, styling and palette paths, the entity's
// own name plus its property for the three entity families, start-cased otherwise.
export const labelForChange = (
    { path = '', id, label: rawLabel, type } = {},
    { propertyOnly = false } = {}
) => {
    const label = resolveLabel(rawLabel);
    if (PATH_LABEL_KEYS[path]) {
        return translate(PATH_LABEL_KEYS[path]);
    }

    if (path.startsWith('styling.palette.')) {
        const colour = path.slice('styling.palette.'.length);
        return PALETTE_LABEL_KEYS[colour]
            ? translate(PALETTE_LABEL_KEYS[colour])
            : startCase(colour);
    }

    if (path.startsWith('fields.')) {
        return entityLabel(path, 'fields.', {
            id,
            label,
            attrLabelKeys: FIELD_ATTR_LABEL_KEYS,
            propertyOnly,
        });
    }

    if (path.startsWith('requestTypes.')) {
        return entityLabel(path, 'requestTypes.', {
            id,
            label,
            attrLabelKeys: REQUEST_TYPE_ATTR_LABEL_KEYS,
            propertyOnly,
        });
    }

    if (path.startsWith('templates.')) {
        // Template properties are named per type: a portal "name" is its Message Name.
        return entityLabel(path, 'templates.', {
            id,
            label,
            attrLabelKeys: TEMPLATE_ATTR_LABEL_KEYS[type],
            propertyOnly,
        });
    }

    const segments = path.split('.');
    return label || startCase(segments[segments.length - 1]);
};

// Stored values are HTML-encoded, so decode before re-escaping for the [html] block.
// unescape runs first and does not handle &apos;, which is why that one is normalized after.
const decodeEntities = value => unescape(String(value)).replace(/&apos;/gi, "'");

// Values go into a BBCode [html] block, so escape HTML and BBCode brackets to
// keep a value like "</html]" or markup from closing the block early.
const escapeValue = value =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\[/g, '&#91;')
        .replace(/]/g, '&#93;');

// Same, plus quote-escaping for use inside an HTML attribute (the title tooltip).
const escapeAttr = value => escapeValue(value).replace(/"/g, '&quot;');

const TRUNCATE_AT = 80;

// Collection values arrive as string arrays or, for orgs, { label, value }
// pairs — show the label (name) for those.
const asText = item => (item && typeof item === 'object' ? item.label ?? item.value ?? '' : item);

// One chip of a row: the value, and the full text as a tooltip when it was cut.
const chip = (style, { className, display, title } = {}) =>
    `<div${className ? ` class="${className}"` : ''} style="${style}"` +
    `${title ? ` title="${title}"` : ''}>${display}</div>`;

// A value as { display, title }: truncated for display, the whole of it kept in the
// tooltip. `name` renders one item; a caller holding stored enums passes its own.
const renderValue = (value, name = asText) => {
    if (value === null || value === undefined || value === '') {
        return { display: '—', title: null };
    }

    const text = decodeEntities(Array.isArray(value) ? value.map(name).join(', ') : name(value));
    if (text.length > TRUNCATE_AT) {
        return {
            display: escapeValue(`${text.slice(0, TRUNCATE_AT)}…`),
            title: escapeAttr(text),
        };
    }

    return { display: escapeValue(text), title: null };
};

// A { option, requestTypes } value: the option in UI language, with the chosen request
// types appended for "specific". Anything unexpected falls back to renderValue.
const renderDisplayOption = value => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return renderValue(value);
    }

    const { option, requestTypes = [] } = value;
    const optionLabel = DISPLAY_OPTION_LABEL_KEYS[option]
        ? translate(DISPLAY_OPTION_LABEL_KEYS[option])
        : startCase(String(option ?? ''));
    const names = requestTypes.map(name => decodeEntities(String(name)));
    const text =
        option === 'specific' && names.length ? `${optionLabel}: ${names.join(', ')}` : optionLabel;

    if (text.length > TRUNCATE_AT) {
        return {
            display: escapeValue(`${text.slice(0, TRUNCATE_AT)}…`),
            title: escapeAttr(text),
        };
    }

    return { display: escapeValue(text), title: null };
};

// The editor hides the stored `o-` prefix on a field's Field Name, so the log does too.
// Field paths only: a template's `name` is free text an admin typed.
const FIELD_NAME_PREFIX = /^o-/;
const isFieldName = (attr, path) =>
    attr === 'name' && typeof path === 'string' && path.startsWith('fields.');

// Names one value of a form change: a boolean as the state it sets, an enum through
// its editor's own label, anything else as the text it already is.
const nameFormValue = (attr, path) => value => {
    if (typeof value === 'boolean') {
        return translate(BOOLEAN_LABEL_KEYS[String(value)]);
    }

    if (isFieldName(attr, path) && typeof value === 'string') {
        return asText(value.replace(FIELD_NAME_PREFIX, ''));
    }

    const labelKey = VALUE_LABEL_KEYS_BY_ATTR[attr]?.[String(value)];

    return labelKey ? translate(labelKey) : asText(value);
};

// The copied form's source name, which the event carries in metadata because its primary
// resource is the new form. Never empty, so translateObject always fills the placeholder.
export const sourceFormNameFormatter = (_value, auditEventData = {}) => {
    const name = auditEventData?.metadata?.sourceFormName;

    return name ? stripBBCodeTags(decodeEntities(name)) : '—';
};

/**
 * The changes of one event, bucketed by the entity each edits. Read off the path, never
 * off the rendered label, or a field named "Address: Line 1" would invent an entity.
 *
 * @param {Array} [changes] - The event's changes, in the order it reported them
 * @returns {Array} `{ key, label, rows }` per group, unlabelled for an ungrouped change
 */
export const groupChanges = (changes = []) => {
    const groups = [];
    const byKey = new Map();

    changes.forEach(change => {
        const entity = entityOf(change);
        if (!entity) {
            groups.push({ key: null, label: null, rows: [change] });
            return;
        }

        const group = byKey.get(entity.key);
        if (group) {
            // Two names on one key means the key is not the entity — the read was ambiguous.
            // Filing this change here would put it under the other field's name.
            if (group.named && entity.named && group.label !== entity.label) {
                groups.push({ key: null, label: null, rows: [change] });
                return;
            }

            group.rows.push(change);
            // The heading is the only place a grouped entity is named, so a row carrying
            // the real name replaces one start-cased out of the key.
            if (!group.named && entity.named) {
                group.label = entity.label;
                group.named = true;
            }
            return;
        }

        const started = {
            key: entity.key,
            label: entity.label,
            named: entity.named,
            rows: [change],
        };
        byKey.set(entity.key, started);
        groups.push(started);
    });

    return groups;
};

// The three spellings of "held nothing" that renderValue already reads as one em dash.
const heldNothing = value => value === null || value === undefined || value === '';

// The chips a change shows: one on its own for an addition or a removal, the struck
// previous value and an arrow before the new one for a modification.
const changeChips = (change, { valueOnly }) => {
    const { action, before, after } = change || {};
    // Display Options carry an object; every other value is named after its property, so
    // a stored boolean or enum reads as the editor words it.
    const render = change?.path?.endsWith('.displayOptions')
        ? renderDisplayOption
        : changeValue =>
              renderValue(changeValue, nameFormValue(changeAttribute(change), change?.path));

    // A translation entry lists queued text rather than a change to it, so no before side.
    // The edit entry it is paired with records the change.
    if (valueOnly || action === 'ADDED') {
        return chip(NEW_CHIP, render(after));
    }

    if (action === 'REMOVED') {
        return chip(OLD_CHIP, render(before));
    }

    // A modification out of nothing is an addition in all but name. Striking an em
    // dash through and pointing an arrow out of it says less than the value alone.
    if (heldNothing(before)) {
        return chip(NEW_CHIP, render(after));
    }

    return (
        chip(OLD_CHIP, render(before)) +
        chip(ARROW, { className: CHANGE_ARROW_CLASS, display: '→' }) +
        chip(NEW_CHIP, render(after))
    );
};

// One change as a row: its label, then the values it moved between.
const renderChangeItem = (change, { propertyOnly, valueOnly }) => {
    const label = escapeValue(decodeEntities(labelForChange(change, { propertyOnly })));

    return (
        `<li style="${ITEM_STYLE}"><div style="${ROW_STYLE}">` +
        `<div style="${ROW_LABEL_STYLE}"><b>${label}</b></div>` +
        `${changeChips(change, { valueOnly })}</div></li>`
    );
};

// An entity's changes under a heading that names it once and counts them.
const renderGroup = ({ label, rows }, { valueOnly }) => {
    const heading =
        `<b>${escapeValue(decodeEntities(label))}</b>&nbsp;&nbsp;` +
        `<span class="${GROUP_COUNT_CLASS}" style="${GROUP_COUNT_STYLE}">${escapeValue(
            translate('activityLog.changeGroup.count', { smart_count: rows.length })
        )}</span>`;
    // The heading states the entity, so the rows under it name the property alone.
    const nested = rows
        .map(row => renderChangeItem(row, { propertyOnly: true, valueOnly }))
        .join('');

    return `<li style="${ITEM_STYLE}">${heading}<ul style="${GROUP_LIST_STYLE}">${nested}</ul></li>`;
};

// metadata.changes[] as a list, an entity's changes gathered under its name. Never empty,
// so translateObject always fills the placeholder. A `propertyOnly` caller has already
// named the entity in its sentence, so its rows stay flat rather than say it twice.
const renderChanges = (changes, { propertyOnly = false, valueOnly = false } = {}) => {
    if (!Array.isArray(changes) || changes.length === 0) {
        return '—';
    }

    const groups = propertyOnly ? [{ rows: changes }] : groupChanges(changes);

    // Event-wide, not per entity: a lone change reads better flat, but once anything else
    // here is headed it needs one too, or it reads as part of the block above it.
    const headed = groups.some(group => group.label && group.rows.length > 1);

    const listItems = groups
        .map(group =>
            group.label && headed
                ? renderGroup(group, { valueOnly })
                : group.rows.map(row => renderChangeItem(row, { propertyOnly, valueOnly })).join('')
        )
        .join('');

    return `[html]<ul style="${LIST_STYLE}">${listItems}</ul>[/html]`;
};

// The changes an event carries; an array may also be passed directly, for reuse.
const changesOf = (value, auditEventData) =>
    Array.isArray(value) ? value : auditEventData?.metadata?.changes;

export const changeFormatter = (value, auditEventData = {}) =>
    renderChanges(changesOf(value, auditEventData));

// The text a save queued for translation, listed rather than diffed — the paired edit
// entry records the change. Written on queueing, so it says nothing about the outcome.
export const translationChangeFormatter = (value, auditEventData = {}) =>
    renderChanges(changesOf(value, auditEventData), { valueOnly: true });

// A created template's sentence already says a template was added, so its rows name
// the property alone rather than repeating the template's name on each one.
export const templateCreatedChangeFormatter = (value, auditEventData = {}) =>
    renderChanges(changesOf(value, auditEventData), { propertyOnly: true });

// A deletion names the template in its sentence rather than striking its name
// through in a chip, so the name is read off the change the event carries.
export const templateNameFormatter = (_value, auditEventData = {}) => {
    const [{ label } = {}] = auditEventData?.metadata?.changes ?? [];

    // The name lands in a sentence the renderer parses as BBCode, so tags are dropped.
    return label ? stripBBCodeTags(decodeEntities(resolveLabel(label))) : '—';
};

/* --- Workflow node events ----------------------------------------------------------------
 * Same `metadata.changes[]` and the same rows as a form event, plus two things a form change
 * does not need: stored config values are enums meaningless on their own, and a few fields
 * hold an object, which reads as a sub-list of its keys.
 */

// Names a stored value through the vocabulary above. A custom form field is named by its own
// path instead, the way its dialog names it; anything neither knows keeps its text.
const getChangeValue = value => {
    if (Array.isArray(value)) {
        return value.map(getChangeValue).join(', ');
    }
    const fieldName = typeof value === 'string' ? getCustomFieldLabelFromPath(value) : null;

    return translateOr(WORKFLOW_VALUE_LABEL_KEYS[String(value)], fieldName ?? value);
};

// Names the key of an object value. A key is an identifier, not customer text, so an unknown
// one is start-cased rather than shown raw.
const getChangeKey = key => translateOr(WORKFLOW_VALUE_LABEL_KEYS[key], startCase(key));

// Names one item of a node config. A branch reads as its condition and the value it matches
// ("is Delete"), taken from `operator` and `value`/`values` alone: any other key the producer
// sends stays out of the log, and the field it tests already has its own row.
const nameConfigValue = item => {
    if (item === null || typeof item !== 'object' || isOptionObject(item)) {
        return getChangeValue(asText(item));
    }
    const { operator, value, values } = item;

    return [operator, value ?? values]
        .filter(part => part !== undefined)
        .map(getChangeValue)
        .join(' ');
};

/** One row: the field, then the value it holds now, after the value it replaced. */
const renderChangeRow = (label, before, after) => {
    const value = side => renderValue(side, nameConfigValue);
    // Nothing to strike through: the field held nothing before, or it did not change.
    const chips =
        before === null || before === undefined || isEqual(before, after)
            ? chip(NEW_CHIP, value(after))
            : chip(OLD_CHIP, value(before)) +
              chip(ARROW, { className: CHANGE_ARROW_CLASS, display: '\u2192' }) +
              chip(NEW_CHIP, value(after));

    return (
        `<li style="${ITEM_STYLE}"><div style="${ROW_STYLE}">` +
        `<div style="${ROW_LABEL_STYLE}"><b>${label}</b></div>${chips}</div></li>`
    );
};

// A field names the previous step's outcome when its last segment is the marker. The marker is
// bare until the parent edge is wired, and `parents.<parentId>.` prefixed after.
const isOutcomePath = path =>
    typeof path === 'string' && path.split('.').pop() === PREVIOUS_OUTCOME_FIELD;

// The marker as a change's own value, as one rule's field, or as any rule's field in a set.
const carriesOutcomeMarker = value => {
    if (Array.isArray(value)) {
        return value.some(carriesOutcomeMarker);
    }

    if (value !== null && typeof value === 'object') {
        return isOutcomePath(value.path);
    }

    return isOutcomePath(value);
};

/**
 * Whether the node branches on whether the previous step succeeded. The marker is read from the
 * value rather than from the path, so the basis shows wherever the producer nests the rule.
 *
 * @param {Array} changes - the event's metadata.changes[]
 * @returns {boolean} true when the node branches on the previous step's outcome
 */
const isPreviousOutcome = changes =>
    changes.some(
        change => carriesOutcomeMarker(change?.before) || carriesOutcomeMarker(change?.after)
    );

// The first sentence of each node event. Keyed by literal event type: importing ./constants
// here would make the two modules a cycle.
const WORKFLOW_NODE_SENTENCE_KEYS = {
    'platform.workflowNodeCreated': 'workflows.audit.nodeCreated',
    'platform.workflowNodeUpdated': 'workflows.audit.nodeUpdated',
    'platform.workflowNodeDeleted': 'workflows.audit.nodeDeleted',
};

// The same sentence with the changes hung off it. A deletion, or an event reporting none,
// keeps the plain one rather than ending on an empty "Changes:".
const WORKFLOW_NODE_SENTENCE_WITH_CHANGES_KEYS = {
    'platform.workflowNodeCreated': 'workflows.audit.nodeCreatedWithChanges',
    'platform.workflowNodeUpdated': 'workflows.audit.nodeUpdatedWithChanges',
};

// One `platform.workflowNode*` event: the sentence, then a row per changed field. Takes the
// whole event, because the event type supplies the verb.
export const workflowNodeChangeFormatter = ({
    auditEventData = {},
    auditEventType,
    auditUser,
} = {}) => {
    const { metadata, name, nodeType } = auditEventData;

    // A node with no name of its own is named by its type.
    const step = name || NODE_TYPE_CONFIG[nodeType]?.label;
    const changes = Array.isArray(metadata?.changes) ? metadata.changes : [];

    // A branch on the previous step's outcome is a fixed split the user never picked, so it
    // states that and shows no rows.
    const outcomeBasis =
        isBranchingNodeType(nodeType) && isPreviousOutcome(changes)
            ? translate('workflows.audit.previousOutcomeBasis')
            : '';

    const rows = (outcomeBasis ? [] : changes)
        .filter(change => change?.path && !HIDDEN_FIELDS.has(change.path))
        .map(({ after, before, path }) => {
            const label = escapeValue(labelForChange({ path }));

            // A field that holds an object reads as a sub-list of the keys that changed — an
            // actionConfig as its request types — and every other field as one row.
            if (!isFlatObject(before) && !isFlatObject(after)) {
                return renderChangeRow(label, before, after);
            }

            const previous = isFlatObject(before) ? before : {};
            const next = isFlatObject(after) ? after : {};
            const keyRows = unionKeys(previous, next)
                // An unchanged key is not a change; a new node's previous side is empty.
                .filter(key => !isEqual(previous[key], next[key]))
                .map(key =>
                    renderChangeRow(escapeValue(getChangeKey(key)), previous[key], next[key])
                )
                .join('');

            // No child row means that no key changed, so the field shows nothing at all.
            return keyRows
                ? `<li style="${ITEM_STYLE}"><b>${label}</b>` +
                      `<ul style="${NESTED_LIST_STYLE}">${keyRows}</ul></li>`
                : '';
        })
        .join('');
    const list = rows ? `<ul style="${LIST_STYLE}">${rows}</ul>` : '';

    // Step <node name || node type> was updated by <user>. Changes: <rows>
    const changesKey =
        list && !outcomeBasis
            ? WORKFLOW_NODE_SENTENCE_WITH_CHANGES_KEYS[auditEventType]
            : undefined;
    const sentence = translate(changesKey ?? WORKFLOW_NODE_SENTENCE_KEYS[auditEventType], {
        step: `<b>${renderValue(translate(step ?? 'common.unknown')).display}</b>`,
        user: `<b>${auditUser ? escapeValue(auditUser) : translate('common.unknown')}</b>`,
        changes: list,
    });

    // A sentence that takes no changes still shows any it was given, after itself.
    return `[html]${sentence}${outcomeBasis ? `. ${outcomeBasis}` : ''}${
        changesKey ? '' : list
    }[/html]`;
};
